import React from 'react';
import { Link } from 'react-router-dom';
import TopoMap from './topo-map';

// Static data — easy to edit
const NOW_FALLBACK = {
  city: 'Taipei, Taiwan',
  status: 'Architecting AI systems for jinban.ai · exploring Taipei night markets',
  reading: 'The Diamond Age · Neal Stephenson',
  building: 'jinban.ai — EU sovereign virtual workspace',
  mood: 'caffeinated, contemplative',
};

const GUIDES = [
  {
    slug: 'self-hosting-ai-agents-on-a-single-vps',
    tag: 'Infrastructure',
    title: 'Self-hosting AI agents on a single VPS.',
    desc: 'A pragmatic stack for running open-weight models behind your own domain — Ollama, vLLM, gVisor, and a Caddy reverse proxy.',
    time: '14 min read',
    updated: 'Apr 2026',
  },
  {
    slug: 'taiwan-gold-card-end-to-end',
    tag: 'Residency',
    title: 'The Taiwan Gold Card, end to end.',
    desc: 'How I applied, what the income proof actually requires, and why it beats Portugal\'s D7 if you work in tech.',
    time: '11 min read',
    updated: 'Mar 2026',
  },
  {
    slug: 'nomad-tax-setup-that-survives-an-audit',
    tag: 'Tax',
    title: 'Nomad tax setup that survives an audit.',
    desc: 'Three years of country-hopping, two structures, one accountant who doesn\'t panic. Covers EU, US, and Asian sources.',
    time: '18 min read',
    updated: 'Feb 2026',
  },
];

const BLOG_POSTS = [
  { slug: 'why-we-built-jinban-in-the-eu-first', date: '2026-04-22', title: 'Why we built jinban in the EU first.', excerpt: 'Sovereignty is a product feature, not a compliance line item.' },
  { slug: 'a-week-of-remote-work-from-hualien', date: '2026-04-08', title: 'A week of remote work from Hualien.', excerpt: 'Three cafés, two earthquakes, one shipped feature.' },
  { slug: 'the-case-against-zapier-in-2026', date: '2026-03-19', title: 'The case against Zapier in 2026.', excerpt: 'When AI agents move work, glue stops paying rent.' },
  { slug: 'running-a-4-person-company-across-3-timezones', date: '2026-03-02', title: 'Notes on running a 4-person company across 3 timezones.', excerpt: 'Async-by-default, sync where it matters, never the inverse.' },
  { slug: 'self-hosted-ai-is-finally-good-enough', date: '2026-02-14', title: 'Self-hosted AI is finally good enough.', excerpt: 'My open-weights stack vs the API I was paying for.' },
  { slug: 'leaving-san-francisco-again', date: '2026-01-28', title: 'Leaving San Francisco, again.', excerpt: 'The third time. The reasons changed; the conclusion didn\'t.' },
];

const PORTFOLIO = [
  { slug: 'jinban-ai', name: 'jinban.ai', desc: 'EU sovereign virtual workspace bringing humans + AI agents onto one board. Founder & CTO.', tag: 'Software', year: '2026', size: 'lg', link: 'jinban.ai' },
  { slug: 'jinka', name: 'Jinka', desc: 'Distributed task and contract platform for global teams.', tag: 'Software', year: '2024', size: 'md', link: 'jinka.dev' },
  { slug: 'pocket-coach', name: 'Pocket Coach', desc: 'iOS app · personalized fitness coaching with on-device LLM inference.', tag: 'Mobile', year: '2025', size: 'md', link: 'App Store' },
  { slug: 'routines', name: 'Routines', desc: 'Android · habit-tracking with offline-first sync.', tag: 'Android', year: '2024', size: 'sm', link: 'Play Store' },
  { slug: 'devlog-zach-dev', name: 'devlog.zach.dev', desc: 'Static-site engine I wrote for my own writing. Markdown, RSS, no build step.', tag: 'Side projects', year: '2024', size: 'sm', link: 'github' },
  { slug: 'gvisor-quickstart', name: 'gvisor-quickstart', desc: 'Open-source helper scripts for sandboxing untrusted code on bare-metal Kubernetes.', tag: 'Side projects', year: '2025', size: 'sm', link: 'github' },
  { slug: 'tiny-recorder', name: 'Tiny Recorder', desc: 'iOS · 1-tap voice memos with auto-transcription. 50k installs.', tag: 'Mobile', year: '2023', size: 'sm', link: 'App Store' },
  { slug: 'field-notes', name: 'Field Notes', desc: 'Android offline travel journal with map sync.', tag: 'Android', year: '2023', size: 'sm', link: 'Play Store' },
];

const FILTERS = ['All', 'Software', 'Mobile', 'Android', 'Side projects'];

// === NOW SECTION ===
function NowSection() {
  const [data, setData] = React.useState(NOW_FALLBACK);
  const [now, setNow] = React.useState(new Date());

  React.useEffect(() => {
    // Try to fetch dynamic /now data; fall back to static.
    let alive = true;
    (async () => {
      try {
        const res = await fetch('/now.json', { cache: 'no-store' });
        if (!res.ok) throw new Error('no /now');
        const json = await res.json();
        if (alive && json && json.city) setData({ ...NOW_FALLBACK, ...json });
      } catch (_) { /* keep fallback */ }
    })();
    const tick = setInterval(() => setNow(new Date()), 60_000);
    return () => { alive = false; clearInterval(tick); };
  }, []);

  const dateStr = now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const timeStr = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  return (
    <section className="now" id="now">
      <div className="container">
        <div className="now__head">
          <div>
            <div className="now__title">/ now</div>
            <h2 className="section__title" style={{ marginTop: 8 }}>What I'm <em>up to</em> right now.</h2>
          </div>
          <div className="now__date">{dateStr} · {timeStr} local</div>
        </div>
        <div className="now__grid">
          <TopoMap city={data.city} label={data.city.split(',')[0]} />
          <div className="now__status">
            <div className="now__status-row">
              <span>Location</span>
              <div>
                <strong>{data.city}</strong>
                <p>{data.status}</p>
              </div>
            </div>
            <div className="now__status-row">
              <span>Building</span>
              <div><strong style={{ fontWeight: 500 }}>{data.building}</strong></div>
            </div>
            <div className="now__status-row">
              <span>Reading</span>
              <div><strong style={{ fontWeight: 500 }}>{data.reading}</strong></div>
            </div>
            <div className="now__status-row">
              <span>Mood</span>
              <div><strong style={{ fontWeight: 500, fontFamily: 'var(--font-serif)', fontStyle: 'italic' }}>{data.mood}</strong></div>
            </div>
            <div className="now__edit">
              <span className="now__edit-dot" /> updated automatically · pulls from /now.json
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// === GUIDES ===
function GuidesSection() {
  return (
    <section className="section" id="guides">
      <div className="container">
        <div className="section__head">
          <div>
            <div className="section__label">Guides</div>
            <h2 className="section__title">Long-form, <em>opinionated</em>, road-tested.</h2>
          </div>
          <Link className="section__more" to="/guides-all">all guides →</Link>
        </div>
        <div className="guides__grid">
          {GUIDES.map((g) => (
            <Link key={g.slug} className="guide-card" to={`/guides/${g.slug}`}>
              <div className="guide-card__arrow">→</div>
              <div className="guide-card__tag">{g.tag}</div>
              <div className="guide-card__title">{g.title}</div>
              <div className="guide-card__desc">{g.desc}</div>
              <div className="guide-card__meta">
                <span>{g.time}</span>
                <span>{g.updated}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// === BLOG ===
function BlogSection() {
  return (
    <section className="section blog" id="blog">
      <div className="container">
        <div className="section__head">
          <div>
            <div className="section__label">Writing</div>
            <h2 className="section__title">Notes from the <em>road</em> and the <em>terminal</em>.</h2>
          </div>
          <Link className="section__more" to="/archive">archive · RSS →</Link>
        </div>
        <div className="blog__list">
          {BLOG_POSTS.map((p) => {
            const d = new Date(p.date);
            const dStr = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
            return (
              <Link key={p.slug} className="blog-row" to={`/blog/${p.slug}`}>
                <div className="blog-row__date">{dStr}</div>
                <div className="blog-row__title">{p.title}</div>
                <div className="blog-row__excerpt">{p.excerpt}</div>
                <div className="blog-row__arrow">→</div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// === PORTFOLIO ===
function PortfolioSection() {
  const [filter, setFilter] = React.useState('All');
  const counts = FILTERS.reduce((acc, f) => {
    acc[f] = f === 'All' ? PORTFOLIO.length : PORTFOLIO.filter(p => p.tag === f).length;
    return acc;
  }, {});

  return (
    <section className="section" id="portfolio">
      <div className="container">
        <div className="section__head">
          <div>
            <div className="section__label">Portfolio</div>
            <h2 className="section__title">Things I've <em>shipped</em>.</h2>
          </div>
        </div>
        <div className="portfolio__filters">
          {FILTERS.map(f => (
            <button
              key={f}
              type="button"
              className={`portfolio__filter ${filter === f ? 'is-active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f}<span className="portfolio__filter-count">{counts[f]}</span>
            </button>
          ))}
        </div>
        <div className="portfolio__grid">
          {PORTFOLIO.map((p) => {
            const visible = filter === 'All' || p.tag === filter;
            return (
              <Link
                key={p.slug}
                to={`/portfolio/${p.slug}`}
                className={`port-card port-card--${p.size} ${visible ? '' : 'is-hidden'}`}
              >
                <div className="port-card__head">
                  <div className="port-card__tag">{p.tag}</div>
                  <div className="port-card__year">{p.year}</div>
                </div>
                <div className="port-card__title">{p.name}</div>
                <div className="port-card__desc">{p.desc}</div>
                <div className="port-card__foot">
                  <span className="port-card__link">{p.link} ↗</span>
                  <span>view →</span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// === FOOTER ===
function Footer() {
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer__grid">
          <div className="footer__brand">
            <strong>Zach Phillips-Gary</strong>
            AI &amp; Software Architect. Founder &amp; CTO of Jinka and jinban.ai. Currently writing from somewhere with good coffee.
          </div>
          <div className="footer__col">
            <h4>Elsewhere</h4>
            <ul>
              <li><a href="https://bsky.app" target="_blank" rel="noopener noreferrer">Bluesky ↗</a></li>
              <li><a href="https://linkedin.com" target="_blank" rel="noopener noreferrer">LinkedIn ↗</a></li>
              <li><a href="mailto:hi@zach.dev">Email ↗</a></li>
              <li><Link to="/feed">RSS</Link></li>
            </ul>
          </div>
          <div className="footer__col">
            <h4>Newsletter</h4>
            <form className="footer__news" onSubmit={(e) => e.preventDefault()}>
              <input type="email" placeholder="you@somewhere.eu" />
              <button type="submit">Subscribe</button>
            </form>
            <div className="footer__news-hint">Once a month. Self-hosted AI, nomad logistics, EU tech.</div>
          </div>
        </div>
        <div className="footer__bottom">
          <span>© 2026 Zach Phillips-Gary</span>
          <span>built in public · v0.1</span>
        </div>
      </div>
    </footer>
  );
}

// === APP ===
function App() {
  return (
    <>
      <nav className="nav">
        <div className="container nav__inner">
          <a className="nav__brand" href="#top">
            <div className="nav__avatar">Z</div>
            <span>zach.dev</span>
          </a>
          <div className="nav__links">
            <Link to="/now">/now</Link>
            <Link to="/guides">guides</Link>
            <Link to="/blog">writing</Link>
            <Link to="/portfolio">portfolio</Link>
          </div>
          <div className="nav__status">
            <span className="dot" />
            online
          </div>
        </div>
      </nav>

      <header className="hero" id="top">
        <div className="container">
          <div className="hero__eyebrow">Zach Phillips-Gary</div>
          <h1>AI &amp; Software <em>Architect</em>.</h1>
          <p className="hero__sub">
            Helping businesses ship faster, build smarter, and scale further — from wherever there's good coffee.
          </p>
          <div className="hero__meta">
            <div><strong>Founder &amp; CTO</strong> · jinban.ai</div>
            <div><strong>Founder</strong> · Jinka</div>
            <div><strong>Available</strong> · selective consulting</div>
            <div><a href="#now">scroll to /now ↓</a></div>
          </div>
        </div>
      </header>

      <NowSection />
      <GuidesSection />
      <BlogSection />
      <PortfolioSection />
      <Footer />
    </>
  );
}

export default App;
